import React, { useMemo } from 'react';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import RawHtml from 'common/RawHtml';
import pageActions from 'core/page/actions';
import dialogActions from 'core/dialogs/actions';
import uiActions from 'core/ui/actions';

import buttonLinkStyles from 'styles/buttonLink';
import dialogStyles from './styles/dialog';
import contentStyles from './styles/content';
import headerStyles from './styles/header';
import titleStyles from './styles/title';
import contentWrapperStyles from './styles/contentWrapper';
import bodyStyles from './styles/body';
import footerStyles from './styles/footer';

const styles = (theme) => {
  const common = {
    body: bodyStyles(theme),
    content: {
      ...contentStyles(theme),
      height: 'auto',
    },
    contentWrapper: contentWrapperStyles(theme),
    dialog: dialogStyles(theme),
    footer: footerStyles(theme),
    header: headerStyles(theme),
    title: titleStyles(theme),
  };
  return {
    ...common,
    address: {
      float: 'left',
      padding: [theme.spacing.sm, theme.spacing.sm, 0, 0],
      width: '50%',

      [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
        paddingRight: 0,
        width: '100%',
      },
    },
    button: {
      ...theme.links.link,
      ...theme.links.linkSecondaryBold,
      '&:hover': {
        color: theme.links.link.hoverColor,
        textDecoration: 'underline',
      },
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontFamily: theme.typography.p.fontFamily,
      paddingLeft: 0,
      paddingRight: 0,
    },
    footer: {
      display: 'none',
    },
    link: {
      ...buttonLinkStyles(theme),
      color: theme.links.link.color,
      display: 'inline-block',
      fontWeight: theme.typography.fontWeights.normal,
      margin: [0, 0, 0, theme.spacing.sm],
    },
    strong: {
      fontWeight: theme.typography.fontWeights.bold,
    },
  };
};

export const SelectAddressBare = ({
  acceptAddress,
  activeGroup,
  addressPopupAreaText,
  addressPopupOiginalAddressHeader,
  addressPopupOriginalActionLabel,
  addressPopupRecommendedActionLabel,
  addressPopupRecommendedAddressHeader,
  addressPopupTitle,
  classes,
  close,
  commonClasses,
  firstLine,
  lastLine,
  normalizedFirstLine,
  normalizedLastLine,
  setActiveGroup,
}) => {
  const keyboardGroup = useMemo(() => {
    const [groupName] = activeGroup.split('_');
    return `${groupName}_NAME`;
  }, [activeGroup]);
  return (
    <div className={classnames('modal-select-address', commonClasses, classes.dialog)}>
      <div className={classnames('modal-content', classes.content)}>
        <div className={classnames('modal-header', classes.header)}>
          <h4 className={classnames('modal-title', classes.title)} id="modal-title">
            {addressPopupTitle}
          </h4>
        </div>
        <div className={classnames('modal-body', classes.body)} id="modal-body">
          <div className={classnames('modal-content-wrapper', classes.contentWrapper)}>
            <fieldset>
              <RawHtml type="p">{addressPopupAreaText}</RawHtml>

              <address className={classes.address}>
                <strong className={classes.strong}>
                  <RawHtml type="span">{addressPopupOiginalAddressHeader}</RawHtml>{' '}
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <button
                    type="button"
                    className={classnames('buttonLink btn-edit', classes.link)}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveGroup(keyboardGroup);
                      close();
                    }}
                  >
                    Edit
                  </button>
                </strong>

                <div>
                  {firstLine}
                  <br />
                  {lastLine}
                </div>

                <button
                  type="button"
                  className={classnames('btn btn-tertiary btn-use-original', classes.button)}
                  onClick={() => acceptAddress(false)}
                >
                  <RawHtml type="span">{addressPopupOriginalActionLabel}</RawHtml>
                </button>
              </address>

              <address className={classes.address}>
                <RawHtml type="strong" className={classes.strong}>
                  {addressPopupRecommendedAddressHeader}
                </RawHtml>

                <div>
                  {normalizedFirstLine}
                  <br />
                  {normalizedLastLine}
                </div>

                <button
                  type="button"
                  className={classnames('btn btn-tertiary btn-use-recommended', classes.button)}
                  onClick={() => acceptAddress(true)}
                >
                  <RawHtml type="span">{addressPopupRecommendedActionLabel}</RawHtml>
                </button>
              </address>
            </fieldset>
          </div>
        </div>
        <div className={classnames('modal-footer', classes.footer)} />
      </div>
    </div>
  );
};

SelectAddressBare.propTypes = {
  acceptAddress: PropTypes.func.isRequired,
  activeGroup: PropTypes.string.isRequired,
  addressPopupAreaText: PropTypes.string.isRequired,
  // Note: this resource is spelled wrong at the API level
  addressPopupOiginalAddressHeader: PropTypes.string.isRequired,
  addressPopupOriginalActionLabel: PropTypes.string.isRequired,
  addressPopupRecommendedActionLabel: PropTypes.string.isRequired,
  addressPopupRecommendedAddressHeader: PropTypes.string.isRequired,
  addressPopupTitle: PropTypes.string.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  close: PropTypes.func.isRequired,
  commonClasses: PropTypes.string.isRequired,
  firstLine: PropTypes.string,
  lastLine: PropTypes.string.isRequired,
  normalizedFirstLine: PropTypes.string,
  normalizedLastLine: PropTypes.string.isRequired,
  setActiveGroup: PropTypes.func.isRequired,
};

SelectAddressBare.defaultProps = {
  firstLine: null,
  normalizedFirstLine: null,
};

const mapStateToProps = ({ data, resources, ui }) => {
  const lastLine = `${data.city}, ${data.state} ${data.postalCode}`;
  return {
    activeGroup: ui.activeGroup,
    addressPopupAreaText: resources.addressPopupAreaText,
    addressPopupOiginalAddressHeader: resources.addressPopupOiginalAddressHeader,
    addressPopupOriginalActionLabel: resources.addressPopupOriginalActionLabel,
    addressPopupRecommendedActionLabel: resources.addressPopupRecommendedActionLabel,
    addressPopupRecommendedAddressHeader: resources.addressPopupRecommendedAddressHeader,
    addressPopupTitle: resources.addressPopupTitle,
    firstLine: data.addressLine1,
    lastLine,
    normalizedFirstLine: data.normalizedFirstLine || data.addressLine1,
    normalizedLastLine: data.normalizedLastLine || lastLine,
  };
};

const mapDispatchToProps = {
  acceptAddress: pageActions.acceptAddress,
  close: dialogActions.close,
  setActiveGroup: uiActions.setActiveGroup,
};

const SelectAddressStyled = withStyles(styles)(SelectAddressBare);

export default connect(mapStateToProps, mapDispatchToProps)(SelectAddressStyled);
